import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './CreateNewRetailerAccount.module.scss';
import Input from '../Input';
import Checkbox from '../../common/Checkbox';
import Button from '../../common/Button';
import Select from '../../common/Select';
import { requiredValidator, emailValidator, isFormValid } from '../../../utils/validators';
import PhoneInput from '../PhoneInput';
import { ECOMMERCE_PLATFORMS } from '../../../constants/platforms';

const validators = [
  {
    key: 'name',
    validator: [requiredValidator],
  },
  {
    key: 'admin_contact.first_name',
    validator: [requiredValidator],
  },
  {
    key: 'admin_contact.last_name',
    validator: [requiredValidator],
  },
  {
    key: 'admin_contact.email',
    validator: [requiredValidator, emailValidator],
  },
  {
    key: 'admin_contact.phone',
    validator: [requiredValidator],
  },
];

const billingContactValidation = [
  {
    key: 'billing_contact.first_name',
    validator: [requiredValidator],
  },
  {
    key: 'billing_contact.last_name',
    validator: [requiredValidator],
  },
  {
    key: 'billing_contact.email',
    validator: [requiredValidator, emailValidator],
  },
  {
    key: 'billing_contact.phone',
    validator: [requiredValidator],
  },
];

function CreateNewRetailerAccount(props) {
  let [submitting, setSubmitting] = useState(false);
  let [isSameDetails, setIsSameDetails] = useState(true);
  let [form, setForm] = useState({
    name: '',
    admin_contact: {},
    billing_contact: {},
    website: '',
  });

  const buttonDisabled = () => submitting || validateForm();

  const onSubmit = (e) => {
    e.preventDefault();

    const data = { ...form };
    data.name = data.name.trim();
    data.retailer_name = data.name;
    data.website = data.website.trim();
    data.admin_contact.first_name = data.admin_contact.first_name.trim();
    data.admin_contact.last_name = data.admin_contact.last_name.trim();
    data.admin_contact.email = data.admin_contact.email.trim();

    if (isSameDetails) {
      data.billing_contact = data.admin_contact;
    }

    data.billing_contact.first_name = data.billing_contact.first_name.trim();
    data.billing_contact.last_name = data.billing_contact.last_name.trim();
    data.billing_contact.email = data.billing_contact.email.trim();

    setSubmitting(true);

    props.submit(data);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name.includes('.')) {
      const [groupKey, key] = name.split('.');

      setForm({
        ...form,
        [groupKey]: {
          ...form[groupKey],
          [key]: value,
        },
      });
    } else {
      setForm({
        ...form,
        [name]: value,
      });
    }

    if (submitting) {
      setSubmitting(false);
    }
  };

  const validateForm = () => {
    const newValidators = [...validators];

    if (!isSameDetails) {
      newValidators.push(...billingContactValidation);
    }

    if (form.website !== '') {
      newValidators.push({
        key: 'ecommerce_platform',
        validator: [requiredValidator],
      });
    }

    return !isFormValid(form, newValidators);
  };

  return (
    <div className={styles.container}>
      <form onSubmit={onSubmit} className={styles.form}>
        <h2>Create New Retailer Account</h2>
        <div className={styles.columns}>
          <div className={styles.column}>
            <h3>Business Information</h3>
            <label>
              <span className={styles.requiredField}>Company Name</span>
              <Input
                name="name"
                value={form.name}
                onChange={handleChange}
                validate={[requiredValidator]}
                blur
                marginTop
                marginBottom
                autocomplete="name"
              />
            </label>
            <label>
              <span>Website Address</span>
              <Input
                name="website"
                value={form.website}
                onChange={handleChange}
                marginTop
                marginBottom
                autocomplete="url"
              />
            </label>
            <label>
              <span className={styles.requiredField}>e-Commerce platform</span>
              <Select
                onChange={(value) => setForm({ ...form, ecommerce_platform: value })}
                name="ecommerce_platform"
                options={ECOMMERCE_PLATFORMS}
                value={form.ecommerce_platform}
                blur
                validate={[requiredValidator]}
              />
            </label>
          </div>
          <div className={styles.column}>
            <h3>Business Contact Details</h3>
            <label>
              <span className={styles.requiredField}>First Name</span>
              <Input
                name="admin_contact.first_name"
                value={form.admin_contact.first_name}
                onChange={handleChange}
                validate={[requiredValidator]}
                blur
                marginTop
                marginBottom
                autocomplete="given-name"
              />
            </label>
            <label>
              <span className={styles.requiredField}>Last Name</span>
              <Input
                name="admin_contact.last_name"
                value={form.admin_contact.last_name}
                onChange={handleChange}
                validate={[requiredValidator]}
                blur
                marginTop
                marginBottom
                autocomplete="family-name"
              />
            </label>
            <label>
              <span className={styles.requiredField}>Email Address</span>
              <Input
                name="admin_contact.email"
                value={form.admin_contact.email}
                onChange={handleChange}
                validate={[requiredValidator, emailValidator]}
                blur
                marginTop
                marginBottom
                autocomplete="email"
              />
            </label>
            <label>
              <span className={styles.requiredField}>Phone Number</span>
              <PhoneInput
                name="admin_contact.phone"
                onChange={handleChange}
                placeholder="Phone Number"
                value={form.admin_contact.phone}
                blur
                validate={[requiredValidator]}
                marginTop
                marginBottom
                disabled
              />
            </label>
            <label>
              <Checkbox
                checked={isSameDetails}
                onChange={setIsSameDetails}
                value="Use the same detail for billing contact"
              />
            </label>
          </div>
          {!isSameDetails ? (
            <div className={styles.column}>
              <h3>Billing Contact Details</h3>
              <label>
                <span className={styles.requiredField}>First Name</span>
                <Input
                  name="billing_contact.first_name"
                  value={form.billing_contact.first_name}
                  onChange={handleChange}
                  marginTop
                  blur
                  validate={[requiredValidator]}
                  marginBottom
                  autocomplete="given-name"
                />
              </label>
              <label>
                <span className={styles.requiredField}>Last Name</span>
                <Input
                  name="billing_contact.last_name"
                  value={form.billing_contact.last_name}
                  onChange={handleChange}
                  marginTop
                  blur
                  validate={[requiredValidator]}
                  marginBottom
                  autocomplete="family-name"
                />
              </label>
              <label>
                <span className={styles.requiredField}>Email Address</span>
                <Input
                  name="billing_contact.email"
                  value={form.billing_contact.email}
                  onChange={handleChange}
                  marginTop
                  blur
                  validate={[requiredValidator]}
                  marginBottom
                  autocomplete="email"
                />
              </label>
              <label>
                <span className={styles.requiredField}>Phone Number</span>
                <PhoneInput
                  name="billing_contact.phone"
                  onChange={handleChange}
                  placeholder="Phone Number"
                  value={form.billing_contact.phone}
                  blur
                  validate={[requiredValidator]}
                  marginTop
                  marginBottom
                  disabled
                />
              </label>
            </div>
          ) : null}
        </div>
        <div className={styles.submitButton}>
          <Button type="submit" disabled={buttonDisabled()} text="Create" />
        </div>
      </form>
    </div>
  );
}

export default CreateNewRetailerAccount;

CreateNewRetailerAccount.propTypes = {
  submit: PropTypes.func.isRequired,
};
